/**
 * Collapsible list at the bottom of the invite / suggest dialog: who was already invited to the
 * consortium (Otable users and external emails, with their status) or who you already suggested
 * this entity to — so nobody is invited twice. Invitations nobody answered yet can be reminded
 * (with an optional note): at most 3 reminders, 3 days apart (backend: interact/invitation/reminders.py).
 */
import React, { useState } from 'react';
import useSWR from 'swr';
import {
  Accordion, AccordionDetails, AccordionSummary, Avatar, Box, Button, Chip, CircularProgress, TextField, Tooltip, Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Email as EmailIcon, NotificationsActive as RemindIcon } from '@mui/icons-material';
import { axiosService, fetcher } from 'shared/helpers/axios';

const STATUS = {
  pending: { label: 'Pending', color: 'warning' },
  accepted: { label: 'Accepted', color: 'success' },
  rejected: { label: 'Declined', color: 'default' },
  expired: { label: 'Expired', color: 'default' },
  open: { label: 'Sent', color: 'info' },
  under_review: { label: 'Seen', color: 'info' },
  implemented: { label: 'Acted on', color: 'success' },
};

const NOTE_MAX = 500;

/** The Remind button of one invitation and its note panel ({ button, panel }, or null when it can't be reminded). */
function useRemind({ row, consortiumId, onSent }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);           // { ok, text }
  if (!['pending', 'expired'].includes(row.status)) return null;

  const send = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await axiosService.post('/interact/consortium-invitations/remind/',
        { consortium_id: consortiumId, invitation_id: row.id, note: note.trim() });
      setMsg({ ok: true, text: data.detail }); setOpen(false); setNote('');
      onSent?.();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || 'The reminder could not be sent.' });
    }
    setBusy(false);
  };

  const button = (
    <span>
      <Button size="small" startIcon={<RemindIcon sx={{ fontSize: '16px !important' }} />} disabled={!row.can_remind || busy}
        onClick={() => setOpen((v) => !v)} sx={{ fontSize: 12.5, py: 0.25, minWidth: 0, textTransform: 'none' }}>
        Remind
      </Button>
    </span>
  );
  return { button: row.can_remind ? button : <Tooltip title={row.remind_blocked || ''}>{button}</Tooltip>,
    panel: (open || msg) && (
      <Box sx={{ pl: 5.25, pb: 1 }}>
        {open && (
          <>
            <TextField size="small" fullWidth multiline minRows={2} value={note} disabled={busy}
              onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
              placeholder={`Add a short note to ${row.type === 'email' ? (row.name || row.email) : (row.user?.name || 'them')} (optional)`}
              helperText={`${note.length}/${NOTE_MAX} · we email them a reminder with the consortium's details and how to join`}
              FormHelperTextProps={{ sx: { fontSize: 11.5, mx: 0 } }} inputProps={{ style: { fontSize: 13.5 } }} />
            <Box display="flex" justifyContent="flex-end" gap={1} mt={0.5}>
              <Button size="small" onClick={() => setOpen(false)} disabled={busy} sx={{ fontSize: 12.5 }}>Cancel</Button>
              <Button size="small" variant="contained" onClick={send} disabled={busy} sx={{ fontSize: 12.5 }}
                startIcon={busy ? <CircularProgress size={12} color="inherit" /> : null}>
                Send reminder
              </Button>
            </Box>
          </>
        )}
        {msg && (
          <Typography sx={{ fontSize: 12.5, mt: 0.5, color: msg.ok ? 'success.main' : 'error.main' }}>{msg.text}</Typography>
        )}
      </Box>
    ) };
}

function RemindableRow({ row, consortiumId, onSent, children }) {
  const remind = useRemind({ row, consortiumId, onSent });
  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}>
      <Box display="flex" alignItems="center" gap={1.25} py={0.75}>
        {children}
        {remind?.button}
      </Box>
      {remind?.panel}
    </Box>
  );
}

function PlainRow({ children }) {
  return (
    <Box display="flex" alignItems="center" gap={1.25} py={0.75}
      sx={{ borderTop: '1px solid', borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}>
      {children}
    </Box>
  );
}

const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

export default function SentInvitationsPanel({ open, mode, entityType, entityId, accent }) {
  const suggest = mode === 'suggest';
  const url = !open || !entityId ? null
    : suggest ? `/interact/suggestions/sent-for-entity/${entityType}/${entityId}/`
      : entityType === 'consortium' ? `/interact/consortium-invitations/sent-for-consortium/?consortium_id=${entityId}`
        : null;
  const { data, error, isLoading, mutate } = useSWR(url, fetcher, { revalidateOnFocus: false });
  if (!url || error) return null;            // e.g. not a member: the list is not shown
  const rows = Array.isArray(data) ? data : [];
  const emails = rows.filter((r) => r.type === 'email').length;
  const title = suggest ? 'You already suggested this to' : 'Already invited';

  return (
    <Accordion disableGutters elevation={0}
      sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, '&::before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.75, alignItems: 'center', gap: 1 } }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{title}</Typography>
        {isLoading ? <CircularProgress size={14} /> : (
          <Chip size="small" label={rows.length} sx={{ height: 20, bgcolor: accent ? `${accent}33` : undefined }} />
        )}
        {!suggest && emails > 0 && (
          <Typography variant="caption" color="text.secondary">({emails} by email)</Typography>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, maxHeight: 260, overflowY: 'auto' }}>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {suggest ? 'You have not suggested this to anyone yet.' : 'Nobody has been invited yet.'}
          </Typography>
        ) : rows.map((r) => {
          const st = STATUS[r.status] || { label: r.status, color: 'default' };
          const name = r.type === 'email' ? (r.name || r.email) : (r.user?.name || 'Otable user');
          const Row = suggest ? PlainRow : RemindableRow;
          return (
            <Row key={r.id} row={r} consortiumId={entityId} onSent={() => mutate()}>
              {r.type === 'email' ? (
                <Avatar sx={{ width: 30, height: 30, bgcolor: 'action.selected', color: 'text.secondary' }}><EmailIcon sx={{ fontSize: 17 }} /></Avatar>
              ) : (
                <Avatar src={r.user?.avatar_url || undefined} sx={{ width: 30, height: 30, fontSize: 13 }}>{(name || '?')[0]}</Avatar>
              )}
              <Box flex={1} minWidth={0}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{name}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {[r.type === 'email' ? (r.name ? `${r.email} · not on Otable yet` : 'not on Otable yet') : 'Otable user',
                    day(r.created), !suggest && r.invited_by && `by ${r.invited_by}`,
                    !suggest && r.reminders > 0 && `reminded ${r.reminders}× (last ${day(r.last_reminded)})`].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <Chip size="small" label={st.label} color={st.color} variant="outlined" sx={{ height: 22, fontSize: 11.5 }} />
            </Row>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
}
